import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Narrow escape hatch for Supabase inference failures: callers keep explicit row
// and payload types while the query builder stays usable for signal cleanup.
export interface TypedSupabaseResult<Row> {
  data: Row[] | null;
  error: PostgrestError | null;
  count?: number | null;
}

export interface TypedSupabaseSingleResult<Row> {
  data: Row | null;
  error: PostgrestError | null;
}

export interface TypedSupabaseValueResult<Value> {
  data: Value | null;
  error: PostgrestError | null;
}

export interface TypedSupabaseQuery<Row> extends PromiseLike<TypedSupabaseResult<Row>> {
  eq(column: string, value: unknown): TypedSupabaseQuery<Row>;
  neq(column: string, value: unknown): TypedSupabaseQuery<Row>;
  gt(column: string, value: unknown): TypedSupabaseQuery<Row>;
  gte(column: string, value: unknown): TypedSupabaseQuery<Row>;
  lt(column: string, value: unknown): TypedSupabaseQuery<Row>;
  lte(column: string, value: unknown): TypedSupabaseQuery<Row>;
  in(column: string, values: readonly unknown[]): TypedSupabaseQuery<Row>;
  contains(column: string, value: unknown): TypedSupabaseQuery<Row>;
  ilike(column: string, value: string): TypedSupabaseQuery<Row>;
  like(column: string, value: string): TypedSupabaseQuery<Row>;
  is(column: string, value: boolean | null): TypedSupabaseQuery<Row>;
  not(column: string, operator: string, value: unknown): TypedSupabaseQuery<Row>;
  or(filters: string): TypedSupabaseQuery<Row>;
  order(column: string, options?: Record<string, unknown>): TypedSupabaseQuery<Row>;
  limit(count: number): TypedSupabaseQuery<Row>;
  range(from: number, to: number): TypedSupabaseQuery<Row>;
  select(columns?: string): TypedSupabaseQuery<Row>;
  single(): PromiseLike<TypedSupabaseSingleResult<Row>>;
  maybeSingle(): PromiseLike<TypedSupabaseSingleResult<Row>>;
}

export type TypedSupabaseMutation<Row> = TypedSupabaseQuery<Row>;

type RawSelectClient = {
  from(table: string): {
    select(columns: string, options?: Record<string, unknown>): unknown;
    update(payload: Record<string, unknown>): unknown;
    insert(payload: Record<string, unknown> | Record<string, unknown>[]): unknown;
    upsert(payload: Record<string, unknown> | Record<string, unknown>[], options?: Record<string, unknown>): unknown;
    delete(): unknown;
  };
};

type RawRpcClient = {
  rpc(functionName: string, args?: Record<string, unknown>, options?: Record<string, unknown>): unknown;
};

const rawSelectClient = supabase as unknown as RawSelectClient;
const rawRpcClient = supabase as unknown as RawRpcClient;

export function typedSelect<Row>(
  table: string,
  columns: string,
  options?: Record<string, unknown>
): TypedSupabaseQuery<Row> {
  const tableClient = rawSelectClient.from(table);
  return (
    options === undefined
      ? tableClient.select(columns)
      : tableClient.select(columns, options)
  ) as TypedSupabaseQuery<Row>;
}

export function typedUpdate<Row>(
  table: string,
  payload: Record<string, unknown>
): TypedSupabaseMutation<Row> {
  return rawSelectClient.from(table).update(payload) as TypedSupabaseMutation<Row>;
}

/**
 * Turn "row-level security filtered my UPDATE out" into an error the user sees.
 *
 * PostgREST answers a filtered-out UPDATE with 204, no error and no rows. On a
 * table the whole workspace can READ but only the owner can WRITE, a teammate's
 * edit therefore looks exactly like a successful one: `error` is null, and the
 * screen shows a confirmation for a change that never happened.
 *
 * The only way to tell those apart from the client is to ask for the changed
 * row back, so a guarded update ends with `.select('id')` and passes the rows
 * here.
 *
 * Use it wherever a screen offers an edit on a row somebody else may own. Do
 * NOT use it where affecting zero rows is a legitimate outcome: clearing a flag
 * that may already be clear, or a background writer with no user waiting on a
 * confirmation. `src/__tests__/rls-silent-update-guard.guardrail.test.ts` holds
 * those exemptions and the reason for each.
 *
 * @param message What the user is told. Say who may make the change rather than
 *   that something went wrong — the request worked, it just was not theirs.
 */
export function assertRowWasChanged(
  rows: readonly unknown[] | null | undefined,
  message: string,
): void {
  if (!rows || rows.length === 0) throw new Error(message);
}

export function typedInsert<Row>(
  table: string,
  payload: Record<string, unknown> | Record<string, unknown>[]
): TypedSupabaseMutation<Row> {
  return rawSelectClient.from(table).insert(payload) as TypedSupabaseMutation<Row>;
}

export function typedUpsert<Row>(
  table: string,
  payload: Record<string, unknown> | Record<string, unknown>[],
  options?: Record<string, unknown>
): TypedSupabaseMutation<Row> {
  return rawSelectClient.from(table).upsert(payload, options) as TypedSupabaseMutation<Row>;
}

export function typedDelete<Row>(table: string): TypedSupabaseQuery<Row> {
  return rawSelectClient.from(table).delete() as TypedSupabaseQuery<Row>;
}

export function typedRpcRows<Row>(
  functionName: string,
  args?: Record<string, unknown>
): PromiseLike<TypedSupabaseResult<Row>> {
  return rawRpcClient.rpc(functionName, args) as PromiseLike<TypedSupabaseResult<Row>>;
}

export function typedRpc<Row>(
  functionName: string,
  args?: Record<string, unknown>,
  options?: Record<string, unknown>,
): TypedSupabaseQuery<Row> {
  return rawRpcClient.rpc(functionName, args, options) as TypedSupabaseQuery<Row>;
}

export function typedRpcValue<Value>(
  functionName: string,
  args?: Record<string, unknown>
): PromiseLike<TypedSupabaseValueResult<Value>> {
  return rawRpcClient.rpc(functionName, args) as PromiseLike<TypedSupabaseValueResult<Value>>;
}
